import { PositionedModelOwnerController } from '@sas-dvr/ltjs-bird';
import { merge, of, animationFrameScheduler, EMPTY } from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  takeUntil,
  debounceTime,
  withLatestFrom,
  take,
} from 'rxjs/operators';
import {
  BirdLayoutState,
  LayoutItem,
  LayoutState,
} from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { withRetained } from '../util';
import { BirdEpic, BirdEpicDependencies } from './BirdEpic';
import { LayoutRootState } from '@sas-dvr/internal-va-react-core/store/birdLayout/LayoutRootState';
import { ofActionCreatorType } from '../util/rxUtils';
import { EpicActions } from '../actions/epicActions';

function getUpdateLayout(
  controller: PositionedModelOwnerController,
  layoutState: LayoutState,
  position: LayoutItem['position'],
  deps: BirdEpicDependencies
) {
  // Fix edge case where we try to layout and we don't have a report controller.
  // This can occur during the section switching animation since portable only supports one open
  //   section at a time.  The
  const reportController = controller.getReportController();
  if (!reportController) {
    return EMPTY;
  }
  deps.refreshLayoutConstraints(controller, layoutState);
  deps.doBirdLayout(controller, layoutState, position.width, position.height);
  const data = deps.getLayoutState(controller);
  return of(BirdLayoutState.actions.updateState(data));
}

export const addLayoutRootEpic: BirdEpic = (actions$, state$, deps) => {
  const fromRemoveLayoutRoot = (rootController: PositionedModelOwnerController) =>
    actions$.pipe(
      ofActionCreatorType<typeof EpicActions.actions.removeLayoutRoot>(
        EpicActions.actionTypes.RemoveLayoutRoot
      ),
      filter((action) => action.payload === rootController),
      take(1)
    );

  const handleLayout = (rootController: PositionedModelOwnerController, rootId: string) => {
    const setPosition = actions$.pipe(
      ofActionCreatorType<typeof BirdLayoutState.actions.setPosition>(
        BirdLayoutState.actionTypes.setPosition
      ),
      filter((action) => {
        return action.payload.id === rootId;
      }),
      withRetained(rootController),
      takeUntil(fromRemoveLayoutRoot(rootController))
    );

    const notifyInvalidate = actions$.pipe(
      ofActionCreatorType<typeof BirdLayoutState.actions.notifyInvalidateLayout>(
        BirdLayoutState.actionTypes.notifyInvalidateLayout
      ),
      withLatestFrom(setPosition),
      map(([, val]) => val)
    );

    return merge(setPosition, notifyInvalidate).pipe(
      debounceTime(0, animationFrameScheduler),
      mergeMap((action) => {
        return getUpdateLayout(
          rootController,
          state$.value.ui.birdLayout.byId,
          action.payload.data,
          deps
        );
      }),
      withRetained(rootController),
      takeUntil(fromRemoveLayoutRoot(rootController))
    );
  };

  const handleSetConstraints = (rootController: PositionedModelOwnerController, rootId: string) =>
    actions$.pipe(
      ofActionCreatorType<
        | typeof BirdLayoutState.actions.setConstraints
        | typeof BirdLayoutState.actions.setBreadcumbLayout
      >(BirdLayoutState.actionTypes.setConstraints, BirdLayoutState.actionTypes.setBreadcumbLayout),
      debounceTime(0, animationFrameScheduler),
      mergeMap(() => {
        const state = state$.value.ui.birdLayout.byId;
        const layoutData = state[rootId];
        if (!layoutData) {
          return EMPTY;
        }
        const rootPosition = layoutData.position;
        if (!rootPosition || !rootPosition.width || !rootPosition.height) {
          return EMPTY;
        }
        return getUpdateLayout(rootController, state$.value.ui.birdLayout.byId, rootPosition, deps);
      }),
      takeUntil(fromRemoveLayoutRoot(rootController))
    );

  return actions$.pipe(
    ofActionCreatorType<typeof EpicActions.actions.addLayoutRoot>(
      EpicActions.actionTypes.AddLayoutRoot
    ),
    mergeMap((value) => {
      const keyToPosition = value.payload.keyToPosition;
      const controller = value.payload.controller;
      const rootId = keyToPosition ? keyToPosition : deps.getKey(controller);
      const updateLayout$ = handleLayout(controller, rootId);
      const onSetConstraints$ = handleSetConstraints(controller, rootId);
      const addRoot$ = of(LayoutRootState.actions.addRoot(rootId));
      const removeRoot$ = fromRemoveLayoutRoot(controller).pipe(
        map(() => LayoutRootState.actions.removeRoot(rootId))
      );
      return merge(addRoot$, removeRoot$, updateLayout$, onSetConstraints$);
    })
  );
};
